import { useClerk } from '@clerk/clerk-react';
import { lazy, type ReactElement, Suspense, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBootstrapController } from '@/app/AppBootstrapController';
import { AppShellRouter } from '@/app/AppShellRouter';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { useUpdateUserContext } from '@/features/users';
import {
  FeatureFlagsProvider,
  useAppDialogs,
  useAppFeatureFlags,
  useClientActivityMonitor,
  useDocumentVisibility,
  useOrganisationEntitlements,
} from '@/shared/hooks';
import { resolvePlanStatus } from '@/shared/plans/planStatus';
import { getPlanDisplayName, PLAN_PRICE_GBP_MINOR_BY_CODE } from '@flowqa/shared';
import { getStoredContext } from './auth/sessionStorage';
import { useAuth } from './auth/useAuth';
import { getNavItemsFromRoutes } from './navigation/routes/nav';
import { appRouteDefinitions } from './navigation/routes/routes';
import { useNotifications } from './providers/notificationHooks';
import { useBootstrapData } from './shared/hooks/useBootstrapData';
import { useCurrentOrgRole } from './shared/hooks/useCurrentOrgRole';
import { useNavItems } from './shared/hooks/useNavItems';
import type { OrganisationDto } from '@flowqa/shared';

const authBypassClerkEnabled = import.meta.env.VITE_AUTH_BYPASS_CLERK === 'true';

type ClerkOrganisationSyncProps = {
  organisations: Array<Pick<OrganisationDto, 'id' | 'clerkOrganizationId'>>;
  resolvedOrganisationId: string | null;
};

const ClerkOrganisationSync = ({
  organisations,
  resolvedOrganisationId,
}: ClerkOrganisationSyncProps): null => {
  const clerk = useClerk();
  const activeClerkOrganisationId = clerk.organization?.id ?? null;

  useEffect(() => {
    if (!clerk.loaded) {
      return;
    }
    const selectedOrganisation =
      resolvedOrganisationId === null
        ? null
        : (organisations.find((organisation) => organisation.id === resolvedOrganisationId) ??
          null);
    const targetClerkOrganisationId = selectedOrganisation?.clerkOrganizationId ?? null;
    if (resolvedOrganisationId !== null && targetClerkOrganisationId === null) {
      return;
    }
    if (targetClerkOrganisationId === activeClerkOrganisationId) {
      return;
    }
    void clerk.setActive({ organization: targetClerkOrganisationId }).catch(() => {
      // Keep local org selection stable even if Clerk active-org sync fails transiently.
    });
  }, [activeClerkOrganisationId, clerk, organisations, resolvedOrganisationId]);

  return null;
};

const AppDialogHost = lazy(async () => {
  const module = await import('@/app/AppDialogHost');
  return { default: module.AppDialogHost };
});

export const App = (): ReactElement => {
  const {
    session,
    loading: authLoading,
    error: authError,
    signIn,
    signUp,
    signOut,
    refreshSession,
    setSessionDirect,
  } = useAuth();
  const [nonCriticalShellEnabled, setNonCriticalShellEnabled] = useState(false);
  const sessionUserId = session?.userId ?? null;
  const hasAuthenticatedSession = !authLoading && sessionUserId !== null;
  const isDocumentVisible = useDocumentVisibility();
  useClientActivityMonitor({
    authLoading,
    hasSession: session !== null,
  });

  useEffect(() => {
    if (sessionUserId === null) {
      setNonCriticalShellEnabled(false);
      return;
    }
    if (!isDocumentVisible) {
      return;
    }
    if (nonCriticalShellEnabled) {
      return;
    }
    let cancelled = false;
    const idleCallback =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(() => {
            if (!cancelled) {
              setNonCriticalShellEnabled(true);
            }
          })
        : null;
    const fallbackTimer = window.setTimeout(() => {
      if (!cancelled) {
        setNonCriticalShellEnabled(true);
      }
    }, 1200);
    return () => {
      cancelled = true;
      if (idleCallback !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleCallback);
      }
      window.clearTimeout(fallbackTimer);
    };
  }, [isDocumentVisible, nonCriticalShellEnabled, sessionUserId]);

  const bootstrap = useBootstrapData({
    session,
    enabled: hasAuthenticatedSession,
  });
  const { notify } = useNotifications();
  const organisations = bootstrap.organisations;
  const visibleProjects = bootstrap.visibleProjects;
  const { selectedOrganisationId, setSelectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const resolvedOrganisationId =
    selectedOrganisationId ?? session?.lastOrganisationId ?? session?.orgId ?? null;
  const entitlementsQuery = useOrganisationEntitlements(
    nonCriticalShellEnabled && bootstrap.projectsLoaded ? resolvedOrganisationId : null,
  );
  const entitlements = entitlementsQuery.data ?? null;
  const planStatus = useMemo(
    () =>
      resolvePlanStatus({
        entitlements,
        projects: bootstrap.visibleProjects.map((project) => ({
          id: project.id,
          name: project.name,
          slug: project.slug,
          badgeChars: project.badgeChars,
          badgeColor: project.badgeColor,
          badgeEnabled: project.badgeEnabled,
          createdAt: project.createdAt,
        })),
        selectedProjectId,
      }),
    [bootstrap.visibleProjects, entitlements, selectedProjectId],
  );
  const projectPlanStateById = useMemo(
    () => new Map(planStatus.projectOptions.map((project) => [project.id, project])),
    [planStatus.projectOptions],
  );
  const hasScheduledDowngrade =
    entitlements !== null &&
    entitlements.subscription.scheduledPlanCode !== null &&
    PLAN_PRICE_GBP_MINOR_BY_CODE[entitlements.subscription.scheduledPlanCode] <
      PLAN_PRICE_GBP_MINOR_BY_CODE[entitlements.plan.code] &&
    entitlements.subscription.scheduledPlanEffectiveAt instanceof Date &&
    entitlements.subscription.scheduledPlanEffectiveAt.getTime() > Date.now();
  const sidebarPlanName =
    entitlements !== null
      ? `${getPlanDisplayName(entitlements.plan.code)}${hasScheduledDowngrade ? '*' : ''}`
      : null;

  const projectLimit = entitlements?.plan.limits.projectLimit ?? null;
  const canCreateProject = projectLimit ? visibleProjects.length < projectLimit : true;
  const lockProjectSelector = false;

  const { routeDefinitions, featureFlags, featureFlagsLoading } = useAppFeatureFlags({
    routes: appRouteDefinitions,
    userId: sessionUserId,
  });
  const effectiveFeatureFlags = featureFlags;
  const effectiveFlagsLoading = featureFlagsLoading;
  const navRouteDefinitions = routeDefinitions;
  const navItems = useMemo(() => getNavItemsFromRoutes(navRouteDefinitions), [navRouteDefinitions]);
  const { isAdmin: isCurrentOrgAdmin } = useCurrentOrgRole(session?.userId, {
    enabled: resolvedOrganisationId !== null,
    organisationId: resolvedOrganisationId,
  });
  const { navItemsForSession, isAdminUser, isSuperuser } = useNavItems(session, navItems, {
    isAdmin: isCurrentOrgAdmin,
  });
  const isAdmin = isAdminUser(session);
  const isSuperuserUser = isSuperuser(session);

  const updateUserContext = useUpdateUserContext();
  const navigate = useNavigate();
  const dialogs = useAppDialogs({
    session,
    bootstrap,
    navigate: (path: string) => {
      void navigate(path);
    },
    refreshSession,
    setSessionDirect,
    updateUserContext: updateUserContext.mutateAsync,
  });

  const storedProjectId = getStoredContext().lastProjectId;
  const sessionLastOrganisationId = session?.lastOrganisationId ?? null;
  const sessionLastProjectId = session?.lastProjectId ?? null;
  const hasOpenDialogs = dialogs.addProjectDialog.open || dialogs.addOrganisationDialog.open;

  return (
    <FeatureFlagsProvider flags={effectiveFeatureFlags} loading={effectiveFlagsLoading}>
      {!authBypassClerkEnabled ? (
        <ClerkOrganisationSync
          organisations={organisations}
          resolvedOrganisationId={resolvedOrganisationId}
        />
      ) : null}
      <AppBootstrapController
        session={session}
        authLoading={authLoading}
        authError={authError}
        signIn={signIn}
        signUp={signUp}
        signOut={signOut}
        refreshSession={refreshSession}
        setSessionDirect={setSessionDirect}
        isAdmin={isAdmin}
        isSuperuser={isSuperuserUser}
        organisations={organisations}
        bootstrap={{
          projectsLoaded: bootstrap.projectsLoaded,
          projects: bootstrap.projects,
          visibleProjectOptions: bootstrap.visibleProjectOptions,
        }}
        selectedOrganisationId={selectedOrganisationId ?? null}
        selectedProjectId={selectedProjectId ?? null}
        setSelectedOrganisationId={setSelectedOrganisationId}
        setSelectedProjectId={setSelectedProjectId}
        dialogs={dialogs}
        routeDefinitions={routeDefinitions}
        featureFlags={featureFlags}
        effectiveFeatureFlags={effectiveFeatureFlags}
        effectiveFlagsLoading={effectiveFlagsLoading}
        featureFlagsLoading={featureFlagsLoading}
        canCreateProject={canCreateProject}
        storedProjectId={storedProjectId}
        sessionLastOrganisationId={sessionLastOrganisationId}
        sessionLastProjectId={sessionLastProjectId}
        onRouteAddProject={() => {
          if (!canCreateProject) {
            notify({ message: 'Project limit reached', tone: 'orange' });
            return;
          }
          dialogs.openAddProject(true);
        }}
        updateUserContext={updateUserContext.mutate}
      >
        {(state) => (
          <>
            <AppShellRouter
              state={state}
              session={session}
              authError={authError}
              isAdmin={isAdmin}
              isSuperuser={isSuperuserUser}
              navItems={navItemsForSession}
              projects={bootstrap.visibleProjects.map((project) => ({
                id: project.id,
                name: project.name,
                slug: project.slug,
                badgeChars: project.badgeChars,
                badgeColor: project.badgeColor,
                badgeEnabled: project.badgeEnabled,
                locked: projectPlanStateById.get(project.id)?.locked ?? false,
                lockReason: projectPlanStateById.get(project.id)?.lockReason ?? null,
              }))}
              allProjects={bootstrap.projects.map((project) => ({
                id: project.id,
                name: project.name,
                slug: project.slug,
                badgeChars: project.badgeChars,
                badgeColor: project.badgeColor,
                badgeEnabled: project.badgeEnabled,
                locked: projectPlanStateById.get(project.id)?.locked ?? false,
                lockReason: projectPlanStateById.get(project.id)?.lockReason ?? null,
              }))}
              organisations={organisations}
              canCreateProject={canCreateProject}
              planName={sidebarPlanName}
              planCode={entitlements?.plan.code ?? null}
              planAlerts={planStatus.alerts}
              planBadge={planStatus.badge}
              uploadBlockedReason={planStatus.uploadBlockedReason}
              addOns={entitlements?.addOns ?? null}
              lockProjectSelector={lockProjectSelector}
              onAddProject={() => {
                if (!canCreateProject) {
                  notify({ message: 'Project limit reached', tone: 'orange' });
                  return;
                }
                dialogs.openAddProject(true);
              }}
              onAddOrganisation={() => {
                dialogs.openAddOrganisation();
              }}
              onSignOut={signOut}
            />
            {hasOpenDialogs ? (
              <Suspense fallback={null}>
                <AppDialogHost dialogs={dialogs} />
              </Suspense>
            ) : null}
          </>
        )}
      </AppBootstrapController>
    </FeatureFlagsProvider>
  );
};
